import { formatLongRevisionType } from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { CommentarySection } from '../components/CommentarySection.tsx';
import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles, reconciliationPageStyles } from '../styles.ts';
import type { ReconciliationPageProps } from '../types.ts';
import {
  getPreviousCommentariesForAddress,
  getUnmatchedPreviousAlternativeComparables,
} from '../../reconciliation/utils/getPreviousAlternativeComparablesEntries.ts';

export const ReconciliationPage = ({
  subjectAddress,
  reconciliationCommentary,
  highestAndBestUseCommentary,
  feasibilityCommentary,
  alternativeComparablesCommentary,
  totalCostToCureCommentary,
  revisionCommentary,
  previousRevisionCommentaries,
  previousAlternativeComparablesCommentaries,
  previousRevisionsData,
  isRevision,
  revisionType,
  commentaryV2,
  alerts,
  revisionData,
  includeTotalCostToCure,
}: ReconciliationPageProps) => {
  const shouldDisplayAlertCommentarySection = alerts
    .filter(({ status }) => status === 'dismissed')
    .some(({ type }) => commentaryV2.alerts?.[type]);

  const previousRevisionCommentaryEntries = (previousRevisionCommentaries ?? [])
    .map((commentary, index) => ({
      commentary,
      revisionType: previousRevisionsData?.[index]?.revisionType ?? 'revision',
    }))
    .filter(({ commentary }) => commentary.trim().length > 0)
    .reverse();

  const currentComparableAddresses = revisionData?.comparables?.map(({ address }) => address) ?? [];
  const unmatchedPreviousAlternativeComparables = getUnmatchedPreviousAlternativeComparables(
    previousAlternativeComparablesCommentaries,
    currentComparableAddresses,
  );

  return (
    <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
      {includeTotalCostToCure && (
        <CommentarySection
          title="Total Cost to Cure Commentary"
          commentary={totalCostToCureCommentary}
        />
      )}
      {isRevision && (
        <>
          {previousRevisionCommentaryEntries.map(({ commentary, revisionType: prevType }, index) => (
            <View
              key={`previous-revision-commentary-${index}`}
              style={reconciliationPageStyles.section}
            >
              <Text style={fontStyles.h5}>
                Previous {formatLongRevisionType(prevType)} Commentary
              </Text>
              <Text style={fontStyles.body}>{commentary.replace('\n', '\n\n')}</Text>
            </View>
          ))}
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>{revisionType} Commentary</Text>
            <Text style={fontStyles.body}>{revisionCommentary?.replace('\n', '\n\n')}</Text>
          </View>
          {(revisionData?.comparables ?? []).map((comp, index) => {
            const commentary = alternativeComparablesCommentary?.[index];
            const previousCommentaries = getPreviousCommentariesForAddress(
              previousAlternativeComparablesCommentaries,
              comp.address,
            );
            return (
              <View
                key={comp.address || `alternative-comparable-${index}`}
                style={reconciliationPageStyles.section}
              >
                <Text style={fontStyles.bodyBold}>
                  Alternative Comparable {index + 1}: {comp.address || 'N/A'}
                </Text>
                {previousCommentaries.map(
                  ({ commentary: previousCommentary, revisionType: prevType }, historyIndex) => (
                    <View
                      key={`previous-alternative-comparable-${comp.address}-${historyIndex}`}
                      style={reconciliationPageStyles.section}
                    >
                      <Text style={fontStyles.h5}>
                        Previous {formatLongRevisionType(prevType)} Commentary
                      </Text>
                      <Text style={fontStyles.body}>
                        {previousCommentary.replace('\n', '\n\n')}
                      </Text>
                    </View>
                  ),
                )}
                {commentary ? (
                  <Text style={fontStyles.body}>{commentary.replace('\n', '\n\n')}</Text>
                ) : null}
              </View>
            );
          })}
          {unmatchedPreviousAlternativeComparables.map(
            ({ address, commentary, revisionType: prevType }, index) => (
              <View
                key={`unmatched-previous-alternative-comparable-${address}-${index}`}
                style={reconciliationPageStyles.section}
              >
                <Text style={fontStyles.bodyBold}>
                  Previous {formatLongRevisionType(prevType)} Alternative Comparable:{' '}
                  {address || 'N/A'}
                </Text>
                <Text style={fontStyles.body}>{commentary.replace('\n', '\n\n')}</Text>
              </View>
            ),
          )}
        </>
      )}

      {feasibilityCommentary && (
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Feasibility Commentary</Text>
          <Text style={fontStyles.body}>{feasibilityCommentary?.replace('\n', '\n\n')}</Text>
        </View>
      )}
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Reconciliation Commentary</Text>
        <Text style={fontStyles.body}>{reconciliationCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Highest & Best Use Commentary</Text>
        <Text style={fontStyles.body}>{highestAndBestUseCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      {shouldDisplayAlertCommentarySection && (
        <View style={{ ...gridStyles.gridSection, marginRight: 20 }}>
          <Text style={fontStyles.h5}>Notes</Text>
          <List
            type="number"
            items={alerts
              .filter(({ status }) => status === 'dismissed')
              .map(({ type, message }) => (
                <Text key={type} style={fontStyles.body}>
                  <Text style={fontStyles.bodyBold}>{message}&nbsp;</Text>
                  {commentaryV2.alerts?.[type]}
                </Text>
              ))}
          />
        </View>
      )}
    </Tkv2PDFPage>
  );
};
